import React, { useEffect, useRef, useState } from 'react';
import './QuestionDisplay.css';

export const QuestionDisplay = ({
  question,
  onQuestionComplete,
  questionAudioElement,
  totalQuestions,
  variant = 'card',
}) => {
  const internalAudioRef = useRef(null);
  const currentAudioUrlRef = useRef(null);
  const currentBlobRef = useRef(null); // Keep blob in memory
  const hasPlayedRef = useRef(false);
  const currentQuestionIdRef = useRef(null);
  const [audioError, setAudioError] = useState(null);

  useEffect(() => {
    const questionId = question?.sequence_number || question?.question_text;
    const targetAudioEl = questionAudioElement ?? internalAudioRef.current;

    console.log('🎵 Question:', question ? 'exists' : 'null');
    console.log('🎵 Audio:', question?.audio ? `${question.audio.length} chars` : 'MISSING');
    console.log('🎵 Audio target:', questionAudioElement ? 'shared element' : 'internal <audio>');

    // Reset for new question
    if (questionId !== currentQuestionIdRef.current) {
      console.log('🔄 New question, resetting');
      hasPlayedRef.current = false;
      currentQuestionIdRef.current = questionId;
      setAudioError(null);
    }
    
    // Check if audio exists and is valid
    if (!question || !question.audio) {
      console.warn('⚠️ No audio data provided');
      setAudioError('No audio available');
      // Still call onQuestionComplete after delay
      if (onQuestionComplete) {
        setTimeout(() => onQuestionComplete(), 3000);
      }
      return;
    }
    
    // Check audio length (should be at least 1000 chars for valid MP3)
    if (question.audio.length < 1000) {
      console.warn('⚠️ Audio data too short:', question.audio.length, 'chars');
      setAudioError('Invalid audio data');
      if (onQuestionComplete) {
        setTimeout(() => onQuestionComplete(), 3000);
      }
      return;
    }

    if (!targetAudioEl) {
      console.log('⏳ Audio element not ready yet');
      return;
    }
    
    // Only play if we haven't played yet
    if (hasPlayedRef.current) {
      console.log('⏭️ Already played this question');
      return;
    }
    
    hasPlayedRef.current = true;
    console.log('▶️ Starting audio playback...');

    let cleanupAudioEl = targetAudioEl;

    const playAudio = async () => {
      let audioUrlToRevoke = null;
      
      try {
        const audioEl = targetAudioEl;
        cleanupAudioEl = audioEl;
        if (audioEl) {
          audioEl.pause();
          audioEl.currentTime = 0;
          // Clear old handlers
          audioEl.onended = null;
          audioEl.onerror = null;
          audioEl.oncanplay = null;
        }
        
        // Store old URL for cleanup after new one is set
        const oldUrl = currentAudioUrlRef.current;
        
        // Decode base64
        console.log('🎵 Decoding audio...');
        const audioData = atob(question.audio);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        
        console.log('🎵 Audio array size:', audioArray.length);
        
        // Verify MP3 header (ID3 or FF FB/FF F3)
        const header = Array.from(audioArray.slice(0, 4))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ');
        console.log('🎵 Audio header:', header);
        
        // Create blob - keep it in memory
        const blob = new Blob([audioArray], { type: 'audio/mpeg' });
        console.log('🎵 Blob size:', blob.size);
        
        if (blob.size === 0) {
          throw new Error('Blob is empty');
        }
        
        // Keep blob reference to prevent garbage collection
        currentBlobRef.current = blob;
        
        // Create new blob URL
        const audioUrl = URL.createObjectURL(blob);
        console.log('🎵 Blob URL created:', audioUrl);
        currentAudioUrlRef.current = audioUrl;
        
        // Revoke old URL after a delay to ensure new one is set
        if (oldUrl) {
          setTimeout(() => {
            try {
              URL.revokeObjectURL(oldUrl);
              console.log('🗑️ Old blob URL revoked');
            } catch (e) {
              console.warn('⚠️ Error revoking old URL:', e);
            }
          }, 100);
        }
        
        // Set up audio element (reuse audioEl from above)
        if (!audioEl) {
          throw new Error('Audio element not found');
        }
        
        // Set src BEFORE setting handlers
        audioEl.src = audioUrl;
        console.log('🎵 Audio src set to:', audioUrl);
        
        // Set up event handlers AFTER src is set
        audioEl.onloadstart = () => {
          console.log('🎵 Audio loadstart - blob URL is being accessed');
        };
        
        audioEl.onloadedmetadata = () => {
          console.log('✅ Audio metadata loaded, duration:', audioEl.duration);
        };
        
        audioEl.oncanplay = async () => {
          console.log('✅ Can play, duration:', audioEl.duration);
          try {
            await audioEl.play();
            console.log('✅ Playing successfully');
          } catch (playError) {
            // A rejected play() (autoplay policy, transient decode failure)
            // used to dead-end here: 'ended' never fires, so onQuestionComplete
            // never runs and the parent never learns the speaking phase is
            // over — the turn hangs with no mic. Hand the turn forward anyway;
            // the question text is on screen. Mirrors the onerror path below.
            console.error('❌ Play failed:', playError);
            setAudioError(`Play failed: ${playError.message}`);
            if (onQuestionComplete) {
              setTimeout(() => onQuestionComplete(), 2000);
            }
          }
        };
        
        audioEl.onended = () => {
          console.log('✅ Audio ended');
          if (onQuestionComplete) {
            onQuestionComplete();
          }
          // Don't revoke here - let cleanup handle it
        };
        
        audioEl.onerror = (e) => {
          const error = audioEl?.error;
          console.error('❌ Audio error event fired');
          console.error('❌ Error code:', error?.code);
          console.error('❌ Error message:', error?.message);
          console.error('❌ Audio src:', audioEl?.src);
          console.error('❌ Network state:', audioEl?.networkState);
          setAudioError(`Audio playback failed: ${error?.message || 'Unknown error'}`);
          if (onQuestionComplete) {
            setTimeout(() => onQuestionComplete(), 2000);
          }
        };
        
        // Load audio - this will trigger events
        console.log('🎵 Calling audio.load()...');
        audioEl.load();
        
        // Store URL for cleanup
        audioUrlToRevoke = audioUrl;
        
      } catch (error) {
        console.error('❌ Error:', error);
        setAudioError(`Audio error: ${error.message}`);
        hasPlayedRef.current = false;
        
        // Clean up on error
        if (audioUrlToRevoke) {
          URL.revokeObjectURL(audioUrlToRevoke);
        }
        
        if (onQuestionComplete) {
          setTimeout(() => onQuestionComplete(), 2000);
        }
      }
    };
    
    playAudio();
    
    // Cleanup - only revoke if we're actually changing questions or unmounting
    return () => {
      // Only cleanup if question is actually changing
      const currentQuestionId = question?.sequence_number || question?.question_text;
        if (currentQuestionId !== currentQuestionIdRef.current) {
        console.log('🧹 Cleaning up audio for question change');
        const audioEl = cleanupAudioEl;
        if (audioEl) {
          audioEl.pause();
          audioEl.onended = null;
          audioEl.onerror = null;
          audioEl.oncanplay = null;
          audioEl.onloadstart = null;
          audioEl.onloadedmetadata = null;
        }
        if (currentAudioUrlRef.current) {
          // Delay revocation to ensure audio element has finished
          setTimeout(() => {
            try {
              URL.revokeObjectURL(currentAudioUrlRef.current);
              console.log('🗑️ Blob URL revoked in cleanup');
              currentAudioUrlRef.current = null;
              currentBlobRef.current = null; // Clear blob reference
            } catch (e) {
              console.warn('⚠️ Error revoking URL in cleanup:', e);
            }
          }, 1000);
        }
      }
    };
    // Re-run when shared audio element becomes available so TTS always binds to the recording mix element.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- internalAudioRef identity is stable; optional refetch guarded by hasPlayedRef
  }, [question?.sequence_number, question?.question_text, questionAudioElement]);

  if (!question) {
    return null;
  }

  const qNum = question.question_order || question.sequence_number || 1;
  const progressLabel = `Question ${qNum}${totalQuestions ? ` of ${totalQuestions}` : ''}`;
  const progressPct = totalQuestions
    ? Math.min(100, (Number(qNum) / Number(totalQuestions)) * 100)
    : 0;

  if (variant === 'panel') {
    return (
      <div className="interview-action-card interview-action-card--question">
        <p className="interview-panel-question-text">{question.question_text}</p>
        <div className="interview-panel-question-hint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" aria-hidden>
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
          <span>Listen carefully before answering</span>
        </div>
        {audioError && (
          <div className="audio-error" style={{ color: '#fecaca', fontSize: 12, marginTop: 6 }}>
            ⚠️ {audioError}
          </div>
        )}
        {!questionAudioElement && (
          <audio ref={internalAudioRef} controls style={{ width: '100%', marginTop: 8 }} preload="auto" />
        )}
      </div>
    );
  }

  if (variant === 'sideCard' || variant === 'topBar') {
    const questionText =
      question.question_text || question.text || question.prompt || question.question || '';
    const questionType = question.question_type || question.type || '';

    return (
      <div className={variant === 'sideCard' ? 'question-side-card' : 'question-top-bar-card'}>
        <div className="question-card-meta-row">
          {questionType && (
            <span className="question-card-type-tag">{questionType}</span>
          )}
        </div>
        <p className="question-side-card-text">{questionText}</p>
        <div className="question-side-card-hint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" aria-hidden>
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
          <span>Listen carefully before answering</span>
        </div>
        {audioError && (
          <div className="audio-error" style={{ color: '#fecaca', fontSize: 12, marginTop: 6 }}>
            ⚠️ {audioError}
          </div>
        )}
        {!questionAudioElement && (
          <audio ref={internalAudioRef} controls style={{ width: '100%', marginTop: 8 }} preload="auto" />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 14,
        border: '1px solid #E5E7EB',
        overflow: 'visible',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          padding: '12px 18px',
          borderBottom: '1px solid #F9FAFB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#9CA3AF',
            textTransform: 'uppercase',
            letterSpacing: '.7px',
          }}
        >
          {progressLabel}
        </span>
      </div>
      <div style={{ padding: '16px 18px' }}>
        <p
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#111827',
            lineHeight: 1.65,
            margin: 0,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {question.question_text}
        </p>
      </div>
      <div style={{ padding: '10px 18px', borderTop: '1px solid #F9FAFB', display: 'flex', alignItems: 'center', gap: 7 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" width="13" height="13" aria-hidden>
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>Listen carefully before answering</span>
      </div>

      {audioError && (
        <div
          className="audio-error"
          style={{
            color: 'red',
            padding: '10px',
            margin: '0 18px 10px',
            background: '#fee',
            borderRadius: '4px',
          }}
        >
          ⚠️ {audioError}
        </div>
      )}

      {!questionAudioElement && (
        <audio ref={internalAudioRef} controls style={{ width: '100%', marginTop: '0', padding: '0 18px 12px' }} preload="auto" />
      )}
    </div>
  );
};
